import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  uniqueIndex,
  date,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { employees } from "./business";

// Session storage table for Replit Auth
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// User storage table for Replit Auth with role-based access
export const users = pgTable("users", {
  id: varchar("id").primaryKey().notNull(),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  // Foreign key to agencies table (string UUID)
  agencyId: varchar("agency_id"),

  // Agency Overview
  agencyName: varchar("agency_name"),
  agencyLicenseNumber: varchar("agency_license_number"),
  agencyEntityType: varchar("agency_entity_type"),
  agencyEIN: integer("agency_ein"),
  sicCode: integer("sic_code"),
  establishedDate: date("established_date"),
  specializations: varchar("specializations"),

  // Contact Information
  phone: varchar("phone"),
  contactEmail: varchar("contact_email"),
  website: varchar("website"),
  agencyAddress1: varchar("agency_address"),
  agencyAddress2: varchar("agency_suite"),
  agencyCity: varchar("agency_city"),
  agencyState: varchar("agency_state"),
  agencyZipCode: varchar("agency_zip_code"),
  agencyCounty: varchar("agency_county"),
  agencyCountry: varchar("agency_country").default("United States"),
  agencyLogo: varchar("agency_logo"),

  passwordHash: varchar("password_hash"),
  mustChangePassword: boolean("must_change_password").default(false), // Force password change on first login
  // Last time a broker/admin issued credentials (setup link or temp password) for this user.
  // Used by the Login Management table.
  lastCredentialIssuedAt: timestamp("last_credential_issued_at"),
  // Which kind of credential was last issued — distinguishes the two pre-activation
  // paths in Login Management.
  lastCredentialKind: varchar("last_credential_kind", { enum: ["setup_link", "temp_password"] }),
  // Last successful login (password or 2FA-completed). Login Management shows
  // this once it's newer than the last issued credential.
  lastLoginAt: timestamp("last_login_at"),
  twoFactorEnabled: boolean("two_factor_enabled").notNull().default(false),
  twoFactorSecret: text("two_factor_secret"), // AES-256-GCM ciphertext (v1:iv:ct:tag); null until enrolled
  twoFactorEnrolledAt: timestamp("two_factor_enrolled_at"),
  mustEnroll2fa: boolean("must_enroll_2fa").notNull().default(false), // Forced enrollment after superadmin reset
  // Per-account opt-out: skips both the login 2FA challenge and the
  // privileged-role forced enrollment, even when TWO_FACTOR_ENABLED is on.
  twoFactorExempt: boolean("two_factor_exempt").notNull().default(false),
  role: varchar("role", { enum: ["broker", "employer", "employee", "agency_member", "superadmin", "ga"] })
    .notNull()
    .default("employee"),
  // GA (General Agency) association — set for users with role="ga"
  gaId: varchar("ga_id"),
  clientId: integer("client_id"), // For employer/employee association
  employeeId: integer("employee_id"), // For employee association
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Agency team members table
export const agencyTeamMembers = pgTable("agency_team_members", {
  id: serial("id").primaryKey(),
  userId: varchar("user_id").notNull(), // Will store string user IDs from auth system
  firstName: varchar("first_name").notNull(),
  lastName: varchar("last_name").notNull(),
  email: varchar("email").notNull(),
  role: text("role").notNull(), // "agent", "manager", "admin", "support"
  department: text("department"), // "sales", "underwriting", "claims", "admin"
  phone: varchar("phone"),
  isActive: boolean("is_active").notNull().default(true),
  lastSeen: timestamp("last_seen"),
  status: text("status").notNull().default("available"), // "available", "busy", "away", "offline"
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Agency teams table
export const agencyTeams = pgTable("agency_teams", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id"),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  teamLeadId: integer("team_lead_id").references(() => employees.id, {
    onDelete: "set null",
  }),
  focusArea: varchar("focus_area", { length: 255 }),
  department: text("department"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const agencyTeamAssignments = pgTable(
  "agency_team_assignments",
  {
    id: serial("id").primaryKey(),
    teamId: integer("team_id")
      .notNull()
      .references(() => agencyTeams.id, { onDelete: "cascade" }),
    employeeId: integer("employee_id")
      .notNull()
      .references(() => employees.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    teamEmployeeUnique: uniqueIndex(
      "uq_agency_team_assignments_team_employee"
    ).on(
      table.teamId,
      table.employeeId
    ),
  })
);

// User-specific permission overrides (hybrid permission model)
// Used in combination with role-based permissions
export const userPermissions = pgTable(
  "user_permissions",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    permission: varchar("permission", { length: 100 }).notNull(),
    // 'grant' adds permission, 'revoke' removes permission from role-based set
    type: varchar("type", { enum: ["grant", "revoke"] }).notNull().default("grant"),
    // Who granted this permission and why
    grantedBy: varchar("granted_by"),
    reason: text("reason"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    userPermissionUnique: uniqueIndex("uq_user_permissions_user_perm").on(
      table.userId,
      table.permission
    ),
    userIdIndex: index("idx_user_permissions_user_id").on(table.userId),
  })
);

// Password reset tokens for forgot password flow + initial account setup
export const passwordResetTokens = pgTable(
  "password_reset_tokens",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    token: varchar("token", { length: 64 }).notNull().unique(),
    // 'forgot_password' = self-service reset (1h TTL).
    // 'initial_setup' = broker-issued first-time account setup (7d TTL).
    purpose: varchar("purpose", { enum: ["forgot_password", "initial_setup"] })
      .notNull()
      .default("forgot_password"),
    expiresAt: timestamp("expires_at").notNull(),
    usedAt: timestamp("used_at"), // Set when token is used
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    tokenIndex: index("idx_password_reset_tokens_token").on(table.token),
    userIdIndex: index("idx_password_reset_tokens_user_id").on(table.userId),
    expiresAtIndex: index("idx_password_reset_tokens_expires_at").on(table.expiresAt),
  })
);

// Pending registrations for email verification flow
export const pendingRegistrations = pgTable(
  "pending_registrations",
  {
    id: serial("id").primaryKey(),
    email: varchar("email").notNull(),
    passwordHash: varchar("password_hash").notNull(),
    firstName: varchar("first_name"),
    lastName: varchar("last_name"),
    verificationCode: varchar("verification_code", { length: 6 }).notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    verifiedAt: timestamp("verified_at"), // Set when code is verified
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    emailIndex: index("idx_pending_registrations_email").on(table.email),
    codeIndex: index("idx_pending_registrations_code").on(table.verificationCode),
    expiresAtIndex: index("idx_pending_registrations_expires_at").on(table.expiresAt),
  })
);

// 2FA backup codes — 10 single-use codes per user, bcrypt-hashed, consumed on use
export const twoFactorBackupCodes = pgTable(
  "two_factor_backup_codes",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    codeHash: varchar("code_hash", { length: 100 }).notNull(),
    usedAt: timestamp("used_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    userIdIndex: index("idx_two_factor_backup_codes_user_id").on(table.userId),
  })
);

// 2FA trusted devices — "remember me" cookies, role-based TTL (7d superadmin/broker, 30d employer/employee)
export const twoFactorTrustedDevices = pgTable(
  "two_factor_trusted_devices",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    tokenHash: varchar("token_hash", { length: 100 }).notNull(),
    deviceLabel: varchar("device_label", { length: 255 }),
    ipAddress: varchar("ip_address", { length: 64 }),
    expiresAt: timestamp("expires_at").notNull(),
    lastUsedAt: timestamp("last_used_at"),
    revokedAt: timestamp("revoked_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    userIdIndex: index("idx_two_factor_trusted_devices_user_id").on(table.userId),
    expiresAtIndex: index("idx_two_factor_trusted_devices_expires_at").on(table.expiresAt),
  })
);

// 2FA verification attempts — drives persistent lockout (5 fails / 30 min) across server restarts
export const twoFactorAttempts = pgTable(
  "two_factor_attempts",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    attemptedAt: timestamp("attempted_at").defaultNow().notNull(),
    success: boolean("success").notNull(),
    ipAddress: varchar("ip_address", { length: 64 }),
  },
  (table) => ({
    userAttemptedIndex: index("idx_two_factor_attempts_user_attempted").on(
      table.userId,
      table.attemptedAt
    ),
  })
);

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertAgencyTeamMemberSchema = createInsertSchema(
  agencyTeamMembers
).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAgencyTeamSchema = createInsertSchema(agencyTeams).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertAgencyTeamAssignmentSchema = createInsertSchema(
  agencyTeamAssignments
).omit({
  id: true,
  createdAt: true,
});

export const insertUserPermissionSchema = createInsertSchema(userPermissions).omit({
  id: true,
  createdAt: true,
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
});

export const insertPendingRegistrationSchema = createInsertSchema(pendingRegistrations).omit({
  id: true,
  createdAt: true,
});

export const insertTwoFactorBackupCodeSchema = createInsertSchema(twoFactorBackupCodes).omit({
  id: true,
  createdAt: true,
});

export const insertTwoFactorTrustedDeviceSchema = createInsertSchema(twoFactorTrustedDevices).omit({
  id: true,
  createdAt: true,
});

export const insertTwoFactorAttemptSchema = createInsertSchema(twoFactorAttempts).omit({
  id: true,
});

// Types
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpsertUser = typeof users.$inferInsert;
export type AgencyTeamMember = typeof agencyTeamMembers.$inferSelect;
export type InsertAgencyTeamMember = typeof agencyTeamMembers.$inferInsert;
export type AgencyTeam = typeof agencyTeams.$inferSelect;
export type InsertAgencyTeam = typeof agencyTeams.$inferInsert;
export type AgencyTeamAssignment = typeof agencyTeamAssignments.$inferSelect;
export type InsertAgencyTeamAssignment = typeof agencyTeamAssignments.$inferInsert;
export type UserPermission = typeof userPermissions.$inferSelect;
export type InsertUserPermission = typeof userPermissions.$inferInsert;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = typeof passwordResetTokens.$inferInsert;
export type PendingRegistration = typeof pendingRegistrations.$inferSelect;
export type InsertPendingRegistration = typeof pendingRegistrations.$inferInsert;
export type TwoFactorBackupCode = typeof twoFactorBackupCodes.$inferSelect;
export type InsertTwoFactorBackupCode = typeof twoFactorBackupCodes.$inferInsert;
export type TwoFactorTrustedDevice = typeof twoFactorTrustedDevices.$inferSelect;
export type InsertTwoFactorTrustedDevice = typeof twoFactorTrustedDevices.$inferInsert;
export type TwoFactorAttempt = typeof twoFactorAttempts.$inferSelect;
export type InsertTwoFactorAttempt = typeof twoFactorAttempts.$inferInsert;
